import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "utils/apiClient";

const useAffiliations = () => {
  const [state, setState] = useState({
    affiliations: [],
    userAffiliations: [],
    types: [],
    assignments: [],
    loading: false,
    error: null,
  });

  const authToken = sessionStorage.getItem("authToken");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    }),
    [authToken]
  );

  const getRequest = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.get(url, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        console.error("[useAffiliations:getRequest]", errorMessage);
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  const postRequest = useCallback(
    async (url, payload) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.post(url, payload);
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    []
  );

  const putRequest = useCallback(
    async (url, payload) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.put(url, payload, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    []
  );

  const deleteRequest = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.delete(url, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    []
  );

  const AFF_BASE = "profile-service/api/v1/affiliations";

  const getAssignmentBase = (userId) => `profile-service/api/v1/users/${userId}/affiliations`;


  const fetchAffiliations = useCallback(async () => {
    try {
      const res = await getRequest(AFF_BASE);
      const items = res || [];
      setState((prev) => ({ ...prev, affiliations: items, loading: false }));
      return items;
    } catch (err) {
      console.error("[useAffiliations] fetchAffiliations", err);
      throw err;
    }
  }, [getRequest]);
  // console.log("useraffiliations state", state.affiliations);
  
  
  const fetchActiveAffiliations = useCallback(async () => {
    try {
      const res = await getRequest(`${AFF_BASE}/active`);
      const items = res.content || res || [];
      setState((prev) => ({ ...prev, affiliations: items, loading: false }));
      return items;
    } catch (err) {
      console.error("[useAffiliations] fetchActiveAffiliations", err);
      throw err;
    }
  }, [getRequest]);

  const getAffiliationById = useCallback(
    async (id) => {
      if (!id) throw new Error("Affiliation ID is required");
      return getRequest(`${AFF_BASE}/${id}`);
    },
    [getRequest]
  );

  const getAffiliationByCode = useCallback(
    async (code) => {
      if (!code) throw new Error("Affiliation code is required");
      return getRequest(`${AFF_BASE}/code/${code}`);
    },
    [getRequest]
  );

  const createAffiliation = useCallback(
    async (payload) => {
      try {
        const res = await postRequest(AFF_BASE, payload);
        await fetchAffiliations();
        return res;
      } catch (err) {
        throw err;
      }
    },
    [postRequest, fetchAffiliations]
  );

  const updateAffiliation = useCallback(
    async (id, payload) => {
      if (!id) throw new Error("Affiliation ID is required");
      try {
        const res = await putRequest(`${AFF_BASE}/${id}`, payload);
        await fetchAffiliations();
        return res;
      } catch (err) {
        throw err;
      }
    },
    [putRequest, fetchAffiliations]
  );

  const deleteAffiliation = useCallback(
    async (id) => {
      if (!id) throw new Error("Affiliation ID is required");
      try {
        const res = await deleteRequest(`${AFF_BASE}/${id}`);
        await fetchAffiliations();
        return res;
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest, fetchAffiliations]
  );



  const fetchUserAffiliations = useCallback(
    async (userId) => {
      if (!userId) throw new Error("userId is required");
      try {
        const base = getAssignmentBase(userId);
        const res = await getRequest(base);
        const items = res.content || res || [];
        setState((prev) => ({ ...prev, userAffiliations: items, loading: false }));
        return items;
      } catch (err) {
        console.error("[useAffiliations] fetchUserAffiliations", err);
        throw err;
      }
    },
    [getRequest]
  );

  //New method to assign affiliation to user without setState
  const justFetchUserAffiliations = useCallback(
    async (userId) => {
      if (!userId) throw new Error("userId is required");
      try {
        const base = getAssignmentBase(userId);
        const res = await getRequest(base);
        const items = res.content || res || [];
        //setState((prev) => ({ ...prev, userAffiliations: items, loading: false }));
        return items;
      } catch (err) {
        console.error("[useAffiliations] fetchUserAffiliations", err);
        throw err;
      }
    },
    [getRequest]
  );

  const assignAffiliationToUser = useCallback(
    async (userId, assignmentPayload) => {
      if (!userId) throw new Error("userId is required");
      try {
        const base = getAssignmentBase(userId);
        const res = await postRequest(base, assignmentPayload);
        await fetchUserAffiliations(userId);
        return res;
      } catch (err) {
        throw err;
      }
    },
    [postRequest, fetchUserAffiliations]
  );

  const getAssignment = useCallback(
    async (userId, assignmentId) => {
      if (!userId || !assignmentId) throw new Error("userId and assignmentId are required");
      const base = getAssignmentBase(userId);
      return getRequest(`${base}/${assignmentId}`);
    },
    [getRequest]
  );

  const updateAssignment = useCallback(
    async (userId, assignmentId, payload) => {
      if (!userId || !assignmentId) throw new Error("userId and assignmentId are required");
      try {
        const base = getAssignmentBase(userId);
        const res = await putRequest(`${base}/${assignmentId}`, payload);
        return res;
      } catch (err) {
        throw err;
      }
    },
    [putRequest]
  );

  const removeAffiliationFromUser = useCallback(
    async (userId, assignmentId) => {
      if (!userId || !assignmentId) throw new Error("userId and assignmentId are required");
      try {
        const base = getAssignmentBase(userId);
        const res = await deleteRequest(`${base}/${assignmentId}`);
        return res;
      } catch (err) {
        throw err;
      }
    },
    [deleteRequest]
  );

  const fetchTypes = fetchAffiliations;
  const createType = createAffiliation;
  const updateType = updateAffiliation;
  const deleteType = deleteAffiliation;
  const addAffiliationToUser = assignAffiliationToUser;

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  return {

    affiliations: state.affiliations,
    userAffiliations: state.userAffiliations,
    types: state.types,
    assignments: state.assignments,
    loading: state.loading,
    error: state.error,

    fetchAffiliations,
    fetchActiveAffiliations,
    getAffiliationById,
    getAffiliationByCode,
    createAffiliation,
    updateAffiliation,
    deleteAffiliation,
    refetchAffiliations: fetchAffiliations,
    justFetchUserAffiliations,

  
    fetchUserAffiliations,
    assignAffiliationToUser,
    getAssignment,
    updateAssignment,
    removeAffiliationFromUser,

    fetchTypes,
    createType,
    updateType,
    deleteType,
    addAffiliationToUser,
  };
};

export default useAffiliations;
